// src/pages/doctor/DoctorSchedule.tsx
import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh-cn';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import api from '../../api/axios';

// 时间标准化：保证 "9:0" -> "09:00"
const toHHMM = (s: string) => {
  const [hRaw, mRaw = '0'] = String(s).split(':');
  const hh = String(Number(hRaw)).padStart(2, '0');
  const mm = String(Number(mRaw)).padStart(2, '0');
  return `${hh}:${mm}`;
};

// 生成时间段（09:00, 09:30...）
const generateTimeSlots = () => {
  const morning = [9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5];
  const afternoon = [13, 13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5];
  const format = (num: number) =>
    `${String(Math.floor(num)).padStart(2, '0')}:${num % 1 === 0 ? '00' : '30'}`;
  return [...morning, ...afternoon].map(format);
};

type ReservationLike = {
  date: string; // "YYYY-MM-DD"
  time: string; // "HH:mm"
  status: string; // assigned/reserved/completed/expired/canceled/doctor_cancelled
};

const deepClone = <T,>(obj: T): T => JSON.parse(JSON.stringify(obj));

const DoctorSchedule: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [weekOffset, setWeekOffset] = useState(0);

  // 当前编辑中的排班：{ 'YYYY-MM-DD': ['HH:MM', ...] }
  const [schedule, setSchedule] = useState<Record<string, string[]>>({});
  // 最近一次从后端取到的排班快照，用来和当前编辑值做 diff
  const baselineRef = useRef<Record<string, string[]>>({});

  // 有预约的时段（assigned/reserved/completed/expired 视为占用）
  const [bookedSlots, setBookedSlots] = useState<Record<string, Set<string>>>({});

  // 拉取医生已设置的排班
  const fetchSchedule = async () => {
    try {
      const response = await api.get('/doctor/schedule');
      const data = (response.data || []) as Array<{ date: string; time: string }>;

      const map: Record<string, string[]> = {};
      data.forEach((item) => {
        const d = String(item.date);
        const t = toHHMM(item.time);
        if (!map[d]) map[d] = [];
        map[d].push(t);
      });

      // 排序一下，UI 观感更稳定
      Object.keys(map).forEach((d) => (map[d] = Array.from(new Set(map[d])).sort()));
      setSchedule(map);
      baselineRef.current = deepClone(map);
    } catch (error: any) {
      const code = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (code === 401 || code === 403) {
        toast.error(
          detail === 'FORBIDDEN_BY_APP_ROLE_CHECK'
            ? (t('auth.forbidden') || '权限不足（角色不匹配）')
            : (t('auth.forbidden') || '权限不足'),
          { autoClose: 3000 }
        );
      } else {
        console.error('Failed to load schedule:', error);
        toast.error(t('doctor.load_failed') || 'スケジュールの読込に失敗しました', { autoClose: 3000 });
      }
    }
  };

  // 拉取“已被预约”的时段（assigned/reserved/completed/expired 都算占用）
  const refreshBookedSlots = async () => {
    try {
      const res = await api.get('/doctor/reservations');
      const map: Record<string, Set<string>> = {};
      (res.data as ReservationLike[]).forEach((r) => {
        if (['assigned', 'reserved', 'completed', 'expired'].includes(r.status)) {
          const d = String(r.date);
          const t = toHHMM(r.time);
          if (!map[d]) map[d] = new Set<string>();
          map[d].add(t);
        }
      });
      setBookedSlots(map);
    } catch (error) {
      // 静默即可（不影响主流程）
      console.warn('Failed to load reservations for booked slots:', error);
    }
  };

  // 总是拿最新（避免“点了没反应/要点很多下”的感觉）
  const ensureFreshBooked = async () => {
    await refreshBookedSlots();
  };

  useEffect(() => {
    dayjs.locale(i18n.language === 'zh' ? 'zh-cn' : 'ja');
    // 首次进入：拉取排班 + 已约数据
    fetchSchedule();
    refreshBookedSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    // 切周时被动刷新一次“已约”
    ensureFreshBooked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  const dates = Array.from({ length: 7 }, (_, i) =>
    dayjs().add(weekOffset * 7 + i, 'day').format('YYYY-MM-DD')
  );
  const times = generateTimeSlots();

  const hasActiveReservationLocal = (date: string, time: string) =>
    bookedSlots[date]?.has(toHHMM(time)) ?? false;

  // —— 关键点：乐观更新 + 需要时回滚 —— //
  const toggleSlot = async (date: string, time: string) => {
    const normTime = toHHMM(time);
    const wasSelected = !!schedule[date]?.includes(normTime);

    // 1) 先“本地”立即切换（立刻有反馈）
    setSchedule((prev) => {
      const daySlots = prev[date] || [];
      const exists = daySlots.includes(normTime);
      const updated = exists ? daySlots.filter((t) => t !== normTime) : [...daySlots, normTime];
      return { ...prev, [date]: updated.sort() };
    });

    // 2) 如果是取消勾选，需要拿最新占用判断是否需要二次确认
    if (wasSelected) {
      await ensureFreshBooked();

      if (hasActiveReservationLocal(date, normTime)) {
        const msg =
          t('doctor.slot_has_reservation_confirm') ||
          '该时间已有用户预约，确定要解除吗？（This timeslot already has a reservation. Remove anyway?）';

        // 用户取消：把刚才的 UI 改动“回滚”回选中状态
        if (!window.confirm(msg)) {
          setSchedule((prev) => {
            const daySlots = prev[date] || [];
            if (daySlots.includes(normTime)) return prev; // 已恢复
            return { ...prev, [date]: [...daySlots, normTime].sort() };
          });
          return;
        }
      }
    }
  };

  const selectAllForDate = async (date: string) => {
    await ensureFreshBooked();

    setSchedule((prev) => {
      const current = prev[date] || [];
      const allSelected = times.every((t) => current.includes(toHHMM(t)));

      if (allSelected) {
        // 执行“全不选” → 若当天包含已预约时段则二次确认
        const hasBookedInDay = times.some((t) => hasActiveReservationLocal(date, toHHMM(t)));
        if (hasBookedInDay) {
          const msg =
            t('doctor.day_has_reservation_confirm') ||
            '该日期包含已被预约的时段，确定要全部解除吗？';
          if (!window.confirm(msg)) return prev;
        }
        return { ...prev, [date]: [] };
      }

      // 全选
      return { ...prev, [date]: times.map(toHHMM) };
    });
  };

  const isSlotSelected = (date: string, time: string) =>
    schedule[date]?.includes(toHHMM(time)) || false;

  const isAllSelectedForDate = (date: string) => {
    const selected = schedule[date] || [];
    return times.every((t) => selected.includes(toHHMM(t)));
  };

  // 保存：优先“增删时段”，失败再整表覆盖，多重兜底；写请求禁用 Cookie（withCredentials:false）
  const handleSave = async () => {
    // 计算 diff
    const datesAll = Array.from(
      new Set([...Object.keys(baselineRef.current), ...Object.keys(schedule)])
    ).sort();

    const addOps: Array<Promise<any>> = [];
    const delOps: Array<Promise<any>> = [];

    for (const date of datesAll) {
      const prevSet = new Set((baselineRef.current[date] || []).map(toHHMM));
      const currSet = new Set((schedule[date] || []).map(toHHMM));

      const toAdd = Array.from(currSet).filter((t) => !prevSet.has(t));
      const toRemove = Array.from(prevSet).filter((t) => !currSet.has(t));

      if (toAdd.length) {
        addOps.push(
          api.post(
            '/doctor/slots',
            { date, times: toAdd },
            { withCredentials: false } // 关键：降低被上游拦截概率
          )
        );
      }
      if (toRemove.length) {
        delOps.push(
          api.delete('/doctor/slots', {
            data: { date, times: toRemove },
            withCredentials: false, // 关键
          })
        );
      }
    }

    // 若没有变化
    if (addOps.length === 0 && delOps.length === 0) {
      toast.info(t('doctor.no_changes') || '没有需要保存的改动', { autoClose: 1800 });
      return;
    }

    try {
      // 并行执行增删
      await Promise.all([...addOps, ...delOps]);
      toast.success(t('doctor.schedule_saved') || '保存成功', { autoClose: 2000 });
      baselineRef.current = deepClone(schedule);
      // 保存后刷新“已约”提示点
      await refreshBookedSlots();
    } catch (err1: any) {
      console.warn('slots diff save failed, fallback to overwrite:', err1);

      // 回退 1：整表覆盖（别名路径）
      const payload = {
        schedules: Object.entries(schedule).flatMap(([date, times]) =>
          (times || []).map((time) => ({ date, time: toHHMM(time) }))
        ),
      };

      try {
        await api.post('/doctor/availability', payload, { withCredentials: false });
        toast.success(t('doctor.schedule_saved') || '保存成功', { autoClose: 2000 });
        baselineRef.current = deepClone(schedule);
        await refreshBookedSlots();
        return;
      } catch (err2) {
        console.warn('availability overwrite failed, fallback to /doctor/schedule:', err2);
      }

      // 回退 2：老路径（PUT 更语义化；若不支持会被 FastAPI 接受为 POST 也可）
      try {
        await api.put('/doctor/schedule', payload, { withCredentials: false });
        toast.success(t('doctor.schedule_saved') || '保存成功', { autoClose: 2000 });
        baselineRef.current = deepClone(schedule);
        await refreshBookedSlots();
        return;
      } catch (err3: any) {
        const code = err3?.response?.status;
        const detail = err3?.response?.data?.detail;
        if (code === 401 || code === 403) {
          toast.error(
            detail === 'FORBIDDEN_BY_APP_ROLE_CHECK'
              ? (t('auth.forbidden') || '权限不足（角色不匹配）')
              : (t('auth.forbidden') || '权限不足'),
            { autoClose: 3000 }
          );
        } else {
          console.error('Failed to save schedule (all fallbacks failed):', err3);
          toast.error(t('doctor.schedule_save_failed') || '保存失败', { autoClose: 3000 });
        }
      }
    }
  };

  const formatWeekday = (dateStr: string) => {
    const dayIndex = dayjs(dateStr).day();
    const zhWeek = ['日', '一', '二', '三', '四', '五', '六'];
    const jaWeek = ['日', '月', '火', '水', '木', '金', '土'];
    return i18n.language === 'zh' ? `周${zhWeek[dayIndex]}` : jaWeek[dayIndex];
  };

  return (
    <div className="px-2 sm:px-6 mt-6 mb-10">
      <div className="text-xl font-bold mb-4">{t('doctor.tab_schedule')}</div>

      <div className="bg-white bg-opacity-95 p-4 rounded-xl shadow-md">
        <div className="flex justify-between mb-4">
          <button
            onClick={() => setWeekOffset((prev) => Math.max(0, prev - 1))}
            disabled={weekOffset === 0}
            className="px-4 py-2 rounded-lg border hover:bg-gray-100 disabled:opacity-30"
          >
            {t('reserve.prev_week')}
          </button>
          <button
            onClick={() => setWeekOffset((prev) => prev + 1)}
            className="px-4 py-2 rounded-lg border hover:bg-gray-100"
          >
            {t('reserve.next_week')}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm md:text-base border table-fixed">
            <thead>
              <tr className="bg-gray-100 text-center">
                <th className="px-2 py-2 border w-[80px]">{t('doctor.time')}</th>
                {dates.map((date) => (
                  <th key={date} className="px-2 py-2 border w-[130px]">
                    <div className="mb-1">
                      {dayjs(date).format('MM/DD')}（{formatWeekday(date)}）
                    </div>
                    <button
                      className="text-xs text-coral hover:underline whitespace-nowrap min-w-[80px] inline-block"
                      onClick={() => selectAllForDate(date)}
                    >
                      {isAllSelectedForDate(date)
                        ? t('doctor.deselect_all_day')
                        : t('doctor.select_all_day')}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {generateTimeSlots().map((time) => (
                <tr key={time}>
                  <td className="text-center font-semibold px-2 py-1 border bg-gray-50">{time}</td>
                  {dates.map((date) => {
                    const selected = isSlotSelected(date, time);
                    const booked = hasActiveReservationLocal(date, time);
                    return (
                      <td
                        key={`${date}-${time}`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleSlot(date, time);
                          }
                        }}
                        onClick={() => toggleSlot(date, time)}
                        className={classNames(
                          'border px-2 py-1 cursor-pointer text-center',
                          selected ? 'bg-green-50' : 'hover:bg-gray-100'
                        )}
                        title={
                          booked
                            ? t('doctor.slot_booked_hint') || '该时段已有预约'
                            : undefined
                        }
                      >
                        {selected ? (
                          <span className="inline-block w-3 h-3 rounded-full bg-green-500 align-middle" />
                        ) : (
                          ''
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={handleSave}
            className="bg-coral text-white px-6 py-3 rounded-full hover:opacity-90 transition"
          >
            {t('doctor.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorSchedule;
